import React, { useEffect, useState } from 'react';
import { IVilla } from '@/types/Villa';
import Image from 'next/image';
import { titleToPath } from '@/lib/utils';
import Bath from '../Icons/Bath';
import Bed from '../Icons/Bed';
import Guest from '../Icons/Guest';
import MapPinIcon from '../Icons/MapPin';
import { FiMessageSquare } from 'react-icons/fi';
import { MdOutlineTimer } from 'react-icons/md';
import RedirectButton from '../RedirectButton';
import { IHomeShortlistModal } from '@/dictionaries/components/modals/types';
import { IExperience } from '@/types/Experience';
import { useCurrency, Currency } from '@/contexts/currencyContext';
import { Locale } from '../../../i18n.config';
import { VillageFeatureTranslation } from '@/services/types';
import { GBP_MULTIPLIER, USD_MULTIPLIER } from '@/utils/currency';
import { formatLocalizedNumber } from '@/utils/village';

interface IShortListVillaItemProps {
  lang: Locale;
  villa: IVilla;
  dict: IHomeShortlistModal;
  itemRemove: (itemType: string, id: number | string) => void;
  setModalIsOpen: (modalIsOpen: boolean) => void;
}

function formatPrice(price: number, lang: Locale, currency: Currency) {
  const priceInt = Math.trunc(price);

  return `${currency.symbol} ${formatLocalizedNumber(priceInt, lang)}`;
}

function changePrice(price: number, savedCurrency: string, currency: string) {
  const eurToUsd = USD_MULTIPLIER;
  const eurToGbp = GBP_MULTIPLIER;

  if (savedCurrency === 'EUR') {
    switch (currency) {
      case 'USD':
        return price * eurToUsd;
      case 'GBP':
        return price * eurToGbp;
      default:
        return price;
    }
  }
  if (savedCurrency === 'USD') {
    switch (currency) {
      case 'EUR':
        return price / eurToUsd;
      case 'GBP':
        return (price / eurToUsd) * eurToUsd;
      default:
        return price;
    }
  }
  if (savedCurrency === 'GBP') {
    switch (currency) {
      case 'EUR':
        return price / eurToGbp;
      case 'USD':
        return (price / eurToGbp) * eurToUsd;
      default:
        return price;
    }
  }
}

export const ShortListVillaItem: React.FC<IShortListVillaItemProps> = ({
  lang,
  villa,
  itemRemove,
  dict,
  setModalIsOpen,
}) => {
  const [price, setPrice] = useState<number>(0);

  const { currency } = useCurrency();

  function getMainFeaturesTranslated() {
    let translatedFeatures: VillageFeatureTranslation[] = [];
    let currentLang = lang;

    if (lang != 'fr' && lang != 'de' && lang != 'it') {
      currentLang = 'en';
    }

    (villa.villageMainFeatures || []).flatMap(({ feature }) => {
      const filtered = feature.featureTranslation.filter((translation) => {
        return translation.locale === currentLang;
      });
      translatedFeatures.push(...filtered);
    });

    return translatedFeatures;
  }

  useEffect(() => {
    const price = changePrice(
      villa.prices.originalPrice,
      villa.prices.currency,
      currency.value,
    ) as number;

    setPrice(price);

    //eslint-disable-next-line
  }, [currency]);

  return (
    <li className="w-full h-fit desktop:h-[380px] flex flex-col desktop:flex-row pointer-events-auto">
      <div className="w-full h-[250px] desktop:h-full relative">
        <Image
          src={villa.media}
          alt={villa.title}
          fill
          sizes="(min-width: 640px) 90vw, (min-width: 1200px) 80vw, 100vw"
          className="w-auto h-full absolute"
        />
      </div>
      <div className="flex flex-col bg-white w-full p-4 desktop:p-[30px]">
        <div className="flex flex-row justify-between">
          <strong className="text-[26px] mb-2 mr-8 font-title">
            {villa?.title}
          </strong>
          <button
            onClick={() => itemRemove('villa', villa.title)}
            className="text-primary flex flex-row items-start space-x-2 justify-end">
            <Image
              src={`https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/stars/fill.svg`}
              alt="Shortlist"
              width={24}
              height={24}
            />
            <span className="hidden w-0 desktop:block desktop:w-fit">
              {dict.items.buttons.remove}
            </span>
          </button>
        </div>

        <div className="flex flex-row items-center space-x-2 text-zinc-400 font-text mb-4">
          <MapPinIcon className="w-4 h-4" />
          <span>
            {villa.city} (
            {dict.items.zones[
              villa.zone.code as
                | 'sicily-east'
                | 'sicily-west'
                | 'sicily-south'
                | 'sicily-north'
            ] || ''}
            )
          </span>
        </div>

        <div className="flex flex-col tablet:flex-row font-noe-regular justify-between items-start my-2.5">
          <div>
            <p>
              {dict.items.price.from}{' '}
              <strong className="text-[26px] font-noe">
                {formatPrice(price, lang, currency)}
              </strong>
              {dict.items.price.perWeek}
            </p>
          </div>

          <div className="flex flex-row items-center justify-end space-x-6 mt-2 ml-auto tablet:ml-4 py-1 tablet:py-0">
            <div className="flex flex-row items-center space-x-2 font-text">
              <Guest className="text-gray-500 w-5 h-5" />
              <span>{villa?.bedrooms}</span>
            </div>
            <div className="flex flex-row items-center space-x-2 font-text">
              <Bath className="text-gray-500 w-5 h-5" />
              <span>{villa?.bathrooms}</span>
            </div>
            <div className="flex flex-row items-center space-x-2 font-text">
              <Bed className="text-gray-500 w-5 h-5" />
              <span>{villa?.sleeps}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col flex-1 justify-between gap-6 desktop:gap-0">
          <div className="bg-gray-100 w-full min-h-[40px] mt-[20px] text-center text-sm leading-[1.71] overflow-hidden text-ellipsis rounded-[5px] text-gray-400 py-[8px] px-[10px] [&>:not(:first-child)]:before:content-['/'] [&>:not(:first-child)]:before:inline-block [&>:not(:first-child)]:before:mx-[8px]">
            {getMainFeaturesTranslated().map(({ title, id }) => (
              <span key={id}>{title}</span>
            ))}
          </div>

          <div className="flex tablet:flex-row tablet:space-x-8 flex-col">
            <RedirectButton
              href={`${dict.items.buttons.villaHref}/${villa.slug}`}
              variant={'outline'}
              onClick={() => setModalIsOpen(false)}>
              {dict.items.buttons.viewVilla}
            </RedirectButton>
            <RedirectButton
              variant={'unanimated'}
              href={`${dict.items.buttons.villaHref}/${villa.slug}#book-section`}
              onClick={() => setModalIsOpen(false)}>
              {dict.items.buttons.book}
            </RedirectButton>
          </div>
        </div>
      </div>
    </li>
  );
};

interface IShortListExperienceItemProps {
  lang: Locale;
  experience: IExperience;
  dict: IHomeShortlistModal;
  itemRemove: (itemType: string, id: number) => void;
  setModalIsOpen: (modalIsOpen: boolean) => void;
}

export const ShortListExperienceItem: React.FC<
  IShortListExperienceItemProps
> = ({ lang, experience, itemRemove, dict, setModalIsOpen }) => {
  const [price, setPrice] = useState<number>(0);

  const { currency } = useCurrency();

  const findTitleSlug = experience.titleSlugs.find((exp) => exp[lang]);
  const titleSlug = findTitleSlug && Object.values(findTitleSlug)[0];

  const categorySlug = dict.items.buttons.categorySlug.find(
    (category) => category.id === experience.categoryId,
  )?.title as string;

  useEffect(() => {
    const price = changePrice(
      experience.price,
      experience.currency,
      currency.value,
    ) as number;

    setPrice(price);

    //eslint-disable-next-line
  }, [currency]);

  return (
    <li className="w-full h-fit desktop:h-[380px] flex flex-col desktop:flex-row pointer-events-auto">
      <div className="w-full h-[250px] desktop:h-full relative">
        <Image
          src={experience.media}
          alt={experience.title}
          fill
          sizes="(min-width: 640px) 75vw, (min-width: 1200px) 33vw, 100vw"
          className="w-auto h-full object-cover absolute"
        />
      </div>
      <div className="flex flex-col bg-white w-full p-[15px] desktop:p-8">
        <div className="flex flex-row justify-between mb-2">
          <strong className="text-2xl font-title">
            {findTitleSlug?.title}
          </strong>
          <button
            onClick={() => itemRemove('experience', experience.id)}
            className="text-primary flex flex-row items-center space-x-2 justify-end ml-3 sm:ml-0">
            <Image
              src={`https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/stars/fill.svg`}
              alt="Shortlist"
              width={24}
              height={24}
            />
            <span className="hidden w-0 desktop:block desktop:w-fit">
              {dict.items.buttons.remove}
            </span>
          </button>
        </div>

        <div className="flex flex-row items-center space-x-2 text-zinc-400 font-text mb-5">
          <MapPinIcon className="w-4 h-4" />
          <span>
            {experience.city
              ? experience.city +
                ` (${
                  dict.items.zones[
                    experience.zone.code as
                      | 'sicily-east'
                      | 'sicily-west'
                      | 'sicily-south'
                      | 'sicily-north'
                  ] || ''
                })`
              : dict.items.zones.yourVilla}
          </span>
        </div>

        <div className="flex flex-row font-title justify-between items-start">
          <div>
            <span className="font-noe-regular">{dict.items.price.from} </span>
            <strong className="text-[26px]">
              {formatPrice(price, lang, currency)}
            </strong>
          </div>
        </div>

        <div className="flex flex-col flex-1 justify-between">
          <div className="flex flex-row h-fit w-full justify-center my-3 gap-6">
            <div className="flex flex-col space-y-4 items-center font-text">
              <MdOutlineTimer className="w-8 h-8 text-zinc-400" />
              <span className="text-center">
                {experience?.duration} {dict.items.experienceProps.hour}
              </span>
            </div>
            <div className="flex flex-col space-y-4 items-center font-text">
              <FiMessageSquare className="w-8 h-8 text-zinc-400" />
              <span className="text-center">
                {experience?.languagens
                  .map((lang) => lang.description)
                  .join(', ')}
              </span>
            </div>
            <div className="flex flex-col space-y-4 items-center font-text">
              <Guest className="w-8 h-8 text-zinc-400" />
              <span className="text-center">
                {experience?.minGuests} - {experience?.maxGuests}{' '}
                {dict.items.experienceProps.people}
              </span>
            </div>
          </div>

          <div className="flex tablet:flex-row flex-col tablet:gap-8">
            <RedirectButton
              href={`${dict.items.buttons.experienceHref}/${categorySlug}/${titleSlug}`}
              variant={'outline'}
              onClick={() => setModalIsOpen(false)}>
              {dict.items.buttons.viewExperience}
            </RedirectButton>
            <RedirectButton
              variant={'unanimated'}
              href={`${dict.items.buttons.experienceHref}/${categorySlug}/${titleSlug}#the-experience`}
              onClick={() => setModalIsOpen(false)}>
              {dict.items.buttons.requestBook}
            </RedirectButton>
          </div>
        </div>
      </div>
    </li>
  );
};
